import { Link } from 'react-router-dom';
import LegalShell from '../components/LegalShell';

const Aup = () => (
  <LegalShell title="Acceptable" accent="Use Policy">

      <section className="legal-section">
        <h2 className="legal-h2">1. Scope</h2>
        <p className="legal-p">
          This Acceptable Use Policy ("AUP") is part of the{' '}
          <Link className="legal-link" to="/terms">Terms of Service</Link> of Qombra B.V. and
          applies to every use of the Qombra platform, the QBrain API and SDK and QAgent (the
          "Services"). Customer ensures that everyone who uses the Services under its account
          complies with it. The lists below are examples; conduct that is comparable in nature or
          effect is prohibited too.
        </p>
      </section>

      <section className="legal-section">
        <h2 className="legal-h2">2. Prohibited AI practices</h2>
        <p className="legal-p">
          The Services may not be used for a practice prohibited by Art. 5 of Regulation (EU)
          2024/1689 (the "AI Act"), in particular to:
        </p>
        <ul className="legal-ul">
          <li>evaluate or classify natural persons based on their social behaviour or personal characteristics, where the resulting score leads to detrimental or disproportionate treatment (social scoring);</li>
          <li>assess or predict the risk of a natural person committing a criminal offence based solely on profiling or on personality traits and characteristics;</li>
          <li>deploy subliminal, manipulative or deceptive techniques, or exploit the vulnerabilities of a person or group, to materially distort behaviour in a way that causes or is likely to cause significant harm;</li>
          <li>infer the emotions of natural persons in the workplace or in education institutions, except for medical or safety reasons;</li>
          <li>categorise natural persons by biometric data to deduce race, political opinions, trade union membership, religious or philosophical beliefs, sex life or sexual orientation;</li>
          <li>build or expand facial recognition databases, or perform real-time remote biometric identification.</li>
        </ul>
      </section>

      <section className="legal-section">
        <h2 className="legal-h2">3. Excluded high-risk uses</h2>
        <p className="legal-p">
          The Services are general-purpose prediction and data-preparation tools and are not
          designed, tested or intended for the uses listed in Annex III of the AI Act. Customer
          shall not use the Services, or any output of the Services, for or as a component of an
          AI system that is used to:
        </p>
        <ul className="legal-ul">
          <li>evaluate the creditworthiness of natural persons or establish their credit score, other than to detect financial fraud;</li>
          <li>assess risk or set prices for natural persons in life or health insurance;</li>
          <li>recruit or select natural persons, including by screening or filtering applications or evaluating candidates, or to take decisions on promotion, termination, task allocation or the monitoring and evaluation of workers;</li>
          <li>determine access to, admission to or assignment within education or vocational training, evaluate learning outcomes, or detect prohibited behaviour during tests;</li>
          <li>evaluate eligibility for public assistance benefits or services, or dispatch or prioritise emergency services, including the triage of patients in emergency healthcare;</li>
          <li>support law enforcement, migration, asylum or border control, or the administration of justice or democratic processes;</li>
          <li>act as a safety component in the management of critical infrastructure (such as digital infrastructure, road traffic, or the supply of water, gas, heating or electricity);</li>
          <li>perform biometric identification, biometric categorisation or emotion recognition;</li>
          <li>serve as a safety component of a product, or as a product, covered by the EU harmonisation legislation listed in Annex I of the AI Act (such as medical devices, machinery or vehicles).</li>
        </ul>
        <p className="legal-p">
          If Customer intends to deploy a model, prediction or workflow derived from the Services
          in one of these fields, Customer must stop and contact Qombra first. Qombra may refuse.
        </p>
      </section>

      <section className="legal-section">
        <h2 className="legal-h2">4. Data</h2>
        <ul className="legal-ul">
          <li>Personal data may be uploaded only under the <Link className="legal-link" to="/dpa">Data Processing Agreement</Link> and only where Customer holds a lawful basis for the processing.</li>
          <li>Special categories of personal data (Art. 9 GDPR, for example health, biometric or genetic data, religion, political opinions, sexual orientation) and data relating to criminal convictions and offences may be uploaded only after Qombra has agreed to it in writing.</li>
          <li>Direct identifiers (such as names, email addresses, national identification numbers and full addresses) are to be removed or pseudonymised before upload where they are not needed for the analysis.</li>
          <li>Data about persons under 16 may be uploaded only where the processing has a lawful basis and complies with the applicable rules on children's data.</li>
          <li>Customer may not upload content that infringes intellectual property rights, trade secrets or other rights of third parties, or that was obtained unlawfully.</li>
        </ul>
      </section>

      <section className="legal-section">
        <h2 className="legal-h2">5. Security and integrity of the Services</h2>
        <ul className="legal-ul">
          <li>Do not probe, scan or test the vulnerability of the Services, or circumvent any authentication, rate limit, quota or other technical safeguard.</li>
          <li>Do not upload malware or code designed to damage, interfere with or gain unauthorised access to any system or data, including through instructions given to QAgent.</li>
          <li>Do not impose an unreasonable load on the infrastructure, for example through automated bulk requests beyond the agreed limits.</li>
          <li>Do not share, publish or transfer API keys, session tokens or passwords outside Customer's organisation, and do not access another customer's account or data.</li>
          <li>Do not use the Services to send unsolicited communications or to operate a botnet, proxy or similar service.</li>
        </ul>
      </section>

      <section className="legal-section">
        <h2 className="legal-h2">6. Protection of the models</h2>
        <ul className="legal-ul">
          <li>Do not reverse engineer, decompile, extract or attempt to reconstruct the models, model weights, prompts or source code of the Services.</li>
          <li>Do not use the Services or their output to train, fine-tune, distil or systematically benchmark a competing model, or to build a dataset for that purpose.</li>
          <li>Do not resell, sublicense or offer the Services to third parties, except as agreed in writing with Qombra.</li>
        </ul>
      </section>

      <section className="legal-section">
        <h2 className="legal-h2">7. Lawful and business use</h2>
        <ul className="legal-ul">
          <li>Use the Services only for Customer's own business purposes and in compliance with the laws that apply to Customer, including data protection, consumer protection, anti-discrimination, financial regulation and export-control law.</li>
          <li>Do not use the Services as a consumer (Art. 6:230g of the Dutch Civil Code).</li>
          <li>Do not use the Services to create or disseminate unlawful, defamatory, discriminatory or misleading content, or to deceive a person into believing they are interacting with a human.</li>
        </ul>
      </section>

      <section className="legal-section">
        <h2 className="legal-h2">8. Enforcement</h2>
        <p className="legal-p">
          Qombra may investigate suspected breaches and, depending on their severity, warn
          Customer, remove content, suspend or restrict access, or terminate the contract for
          cause under clause 11.3 of the Terms. Qombra informs Customer of the measure and its
          reason where the law allows. Customer reports suspected breaches by its users or third
          parties to <a className="legal-link" href="mailto:hey@qombra.com">hey@qombra.com</a>{' '}
          without delay and cooperates in resolving them. Qombra may notify competent authorities
          where required by law.
        </p>
      </section>
  </LegalShell>
);

export default Aup;
